import type { SortOrder } from 'antd/es/table/interface';
import { useCallback } from 'react';
import type { Pagination } from '../../../types';
import type { SorterResult } from '../Table';

export type SorterMap = Record<string, SortOrder | undefined>;

type P<TSorter extends SorterMap> = {
  updateSearchParams?: (params: { pagination?: Partial<Pagination>; sorter?: TSorter }) => void;
};

function parseSortOption<T, TSorter extends SorterMap>(sortOption?: SorterResult<T>): TSorter {
  /* eslint-disable @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion -- Start: This is hack. Return type should be always Record<string, SortOrder | undefined> */
  if (sortOption?.columnKey) {
    return {
      [sortOption.columnKey as string]: sortOption.order,
    } as TSorter;
  }

  return sortOption?.field
    ? ({
        [sortOption.field as string]: sortOption.order,
      } as TSorter)
    : ({} as TSorter);
  /* eslint-enable @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion -- End: This is hack. Return type should be always Record<string, SortOrder | undefined> */
}

function parseSortOptions<T, TSorter extends SorterMap>(sortOptions: Array<SorterResult<T>>): TSorter {
  const result: Record<string, SortOrder | undefined> = {};

  for (const sortOption of sortOptions) {
    for (const [key, value] of Object.entries(parseSortOption<T, TSorter>(sortOption))) {
      result[key] = value;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- TODO: check if this is safe
  return result as TSorter;
}

export const useOnTableChange = <T extends object, TSorter extends SorterMap>({ updateSearchParams }: P<TSorter>) =>
  useCallback(
    (
      newPagination: { current?: number; pageSize?: number },
      _: unknown,
      sorter: Array<SorterResult<T>> | SorterResult<T>,
      extra: { action: 'filter' | 'paginate' | 'sort' },
    ) => {
      if (extra.action === 'paginate') {
        updateSearchParams?.({
          pagination: {
            pageSize: newPagination.pageSize,
            position: newPagination.current,
          },
        });
      }

      if (extra.action === 'sort') {
        const parsedSorter = Array.isArray(sorter)
          ? parseSortOptions<T, TSorter>(sorter)
          : parseSortOption<T, TSorter>(sorter);

        updateSearchParams?.({ sorter: parsedSorter });
      }
    },
    [updateSearchParams],
  );
